import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, Apple, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Smart install button. On click:
 * 1. If `beforeinstallprompt` was captured (Android/Chromium) → triggers the native install prompt directly.
 * 2. If iOS Safari with navigator.share → opens the iOS share sheet (where "Add to Home Screen" is one of the options).
 * 3. Otherwise (or when those fail) → opens a help dialog with step-by-step instructions.
 */
export const InstallAppDialog = ({ trigger }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const onInstalled = () => {
      setDeferredPrompt(null);
      toast.success("App installata sulla home!");
    };
    window.addEventListener("appinstalled", onInstalled);

    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) setDevice("ios");
    else if (/Android/.test(ua)) setDevice("android");
    else setDevice("desktop");

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

  const tryDirectInstall = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Path 1: Native PWA install prompt (Android Chrome, Edge, Samsung Internet, Desktop Chromium)
      if (deferredPrompt) {
        try {
          await deferredPrompt.prompt();
          const choice = await deferredPrompt.userChoice;
          if (choice.outcome === "accepted") {
            toast.success("App installata!");
          }
          setDeferredPrompt(null);
          return;
        } catch {
          // fall through to dialog
        }
      }

      // Path 2: iOS Safari → open share sheet, which contains "Add to Home Screen"
      if (device === "ios" && navigator.share) {
        try {
          await navigator.share({
            title: "Power Fitness Nutrition",
            text: "Aggiungi alla home: tocca Condividi → Aggiungi a Home",
            url: window.location.origin,
          });
          return;
        } catch {
          // user cancelled or not allowed; fall through to dialog with instructions
        }
      }

      // Fallback: open help dialog
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  if (isStandalone) return null;

  // Clone trigger and inject our click handler
  const wrappedTrigger = React.cloneElement(trigger, {
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      tryDirectInstall();
    },
  });

  return (
    <>
      {wrappedTrigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-[#D4AF37]" /> INSTALLA L'APP
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 border border-[#D4AF37]/30 bg-black">
              <img src="/icons/icon-96.png" alt="Power Fitness Nutrition" className="w-14 h-14 rounded-full" />
              <div>
                <div className="font-display font-bold text-lg">Power Fitness Nutrition</div>
                <div className="text-xs text-neutral-400">Premium Supplement Store</div>
              </div>
            </div>

            {device === "ios" && (
              <div>
                <p className="text-sm text-neutral-300 mb-3 flex items-center gap-2"><Apple className="w-4 h-4" /> Su iPhone/iPad il browser non permette l'installazione automatica. Bastano 3 secondi:</p>
                <ol className="space-y-3 text-sm text-neutral-300">
                  <li className="flex gap-3">
                    <span className="text-[#D4AF37] font-bold">1.</span>
                    <span>Apri questa pagina su <strong>Safari</strong> (non funziona in Chrome iOS)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#D4AF37] font-bold">2.</span>
                    <span>Tocca <Share2 className="inline w-4 h-4 mx-1" /><strong>Condividi</strong> in basso</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#D4AF37] font-bold">3.</span>
                    <span>Scorri e scegli <strong>"Aggiungi a Home"</strong> → <strong>Aggiungi</strong></span>
                  </li>
                </ol>
                {navigator.share && (
                  <Button
                    onClick={async () => {
                      try {
                        await navigator.share({ title: "Power Fitness Nutrition", url: window.location.origin });
                        setOpen(false);
                      } catch {}
                    }}
                    className="w-full mt-4 bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-xs h-12"
                  >
                    <Share2 className="w-4 h-4 mr-2" /> Apri Condividi ora
                  </Button>
                )}
              </div>
            )}

            {device === "android" && (
              <div>
                <p className="text-sm text-neutral-300 mb-3">Il tuo browser Android non ha ancora rilevato la possibilità di installazione automatica. Procedi manualmente:</p>
                <ol className="space-y-2 text-sm text-neutral-300 mb-3">
                  <li><span className="text-[#D4AF37] font-bold">1.</span> Apri questa pagina su <strong>Chrome</strong></li>
                  <li><span className="text-[#D4AF37] font-bold">2.</span> Tocca i <strong>3 puntini</strong> (⋮) in alto a destra</li>
                  <li><span className="text-[#D4AF37] font-bold">3.</span> Scegli <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong></li>
                </ol>
                <p className="text-xs text-neutral-500">Naviga 30 secondi e ricarica: il pulsante diretto comparirà.</p>
              </div>
            )}

            {device === "desktop" && (
              <div>
                <p className="text-sm text-neutral-300 mb-3">Stai usando un computer.</p>
                <ol className="space-y-2 text-sm text-neutral-300">
                  <li><span className="text-[#D4AF37] font-bold">PC/Mac</span> — Chrome o Edge: clicca l'icona <Download className="inline w-3.5 h-3.5 mx-1" /> nella barra indirizzo a destra</li>
                  <li><span className="text-[#D4AF37] font-bold">Mobile</span> — apri l'URL su iPhone/Android e usa "Aggiungi a Home"</li>
                </ol>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
